import type { ReactNode } from 'react'

type Tone = 'error' | 'info' | 'warning'

interface CalloutProps {
  tone?: Tone
  title?: string
  children: ReactNode
  className?: string
}

/**
 * DESIGN.md documents no callout, and forbids trading green/red as a button
 * fill. This uses a very light tint with semantic text, keeping the rule that
 * colour lives in the text rather than in a solid fill.
 */
const TONE: Record<Tone, { wrap: string; text: string; icon: ReactNode }> = {
  error: {
    wrap: 'bg-semantic-down/6 border-semantic-down/25',
    text: 'text-semantic-down',
    icon: (
      <path
        d="M8 4.5v4M8 11h.01M8 1.5 1.5 13.5h13L8 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  warning: {
    wrap: 'bg-accent-yellow/8 border-accent-yellow/35',
    text: 'text-ink',
    icon: (
      <path
        d="M8 5v3.5M8 11h.01M14.5 8a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    ),
  },
  info: {
    wrap: 'bg-surface-strong border-hairline',
    text: 'text-body',
    icon: (
      <path
        d="M8 7.5V11M8 5h.01M14.5 8a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    ),
  },
}

export function Callout({ tone = 'info', title, children, className = '' }: CalloutProps) {
  const style = TONE[tone]
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-[16px] border px-4 py-3 flex gap-3 ${style.wrap} ${className}`}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className={`shrink-0 mt-0.5 ${style.text}`}
      >
        {style.icon}
      </svg>
      <div className="min-w-0">
        {title && <p className={`t-title-sm ${style.text}`}>{title}</p>}
        <div className={`t-body-sm ${title ? 'mt-1 text-body' : style.text}`}>{children}</div>
      </div>
    </div>
  )
}
